import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { MdAutoAwesome, MdChevronRight } from 'react-icons/md';
import { IconButton, Spinner, StatusPill } from '@aibuild/ui';
import ProcessAssistantChat from './ProcessAssistantChat';
import {
  AssistantBody,
  AssistantHeader,
  AssistantRail,
  AssistantRailBusy,
  AssistantRoot,
  AssistantStageSlot,
  AssistantTitle,
  AssistantTitleGroup,
} from './ProcessAssistantPanel.styled';
import type { AssistantPanelState } from './useAssistantPanelState';

export interface ProcessAssistantPanelProps {
  processId: string;
  state: AssistantPanelState;
  /** Collapsed state of the process dock — the panel sits to its left. */
  dockCollapsed: boolean;
  /** Called after the assistant saves the graph, to refresh the canvas. */
  onProcessEdited: () => void;
  /** Reports whether a turn is in flight, so the page can freeze the canvas. */
  onBusyChange: (busy: boolean) => void;
  /** Persists pending canvas edits; a false result aborts the send. */
  onBeforeSend: () => Promise<boolean>;
  dataTestId?: string;
}

/**
 * The assistant chat in the editor's right bar.
 *
 * Renders nothing unless {@link AssistantPanelState.available} — the gate and
 * the draft-only rule live in that hook, next to the canvas width they also
 * decide.
 */
const ProcessAssistantPanel: React.FC<ProcessAssistantPanelProps> = ({
  processId,
  state,
  dockCollapsed,
  onProcessEdited,
  onBusyChange,
  onBeforeSend,
  dataTestId = 'process-assistant',
}) => {
  const intl = useIntl();
  const { available, open, chatMounted, toggle: onToggle } = state;

  const [busy, setBusy] = useState(false);
  const reportBusy = useCallback(
    (next: boolean) => {
      setBusy(next);
      onBusyChange(next);
    },
    [onBusyChange],
  );

  if (!available) return null;

  return (
    <AssistantRoot
      $open={open}
      $dockCollapsed={dockCollapsed}
      data-testid={open ? dataTestId : `${dataTestId}__rail`}
    >
      {open ? (
        <AssistantHeader $dockCollapsed={dockCollapsed}>
          <AssistantTitleGroup>
            <AssistantTitle>
              {intl.formatMessage({
                id: 'processes.assistant.title',
                defaultMessage: 'Process assistant',
              })}
            </AssistantTitle>
            <AssistantStageSlot>
              <StatusPill tone="info" dataTestId={`${dataTestId}__stage`}>
                {intl.formatMessage({
                  id: 'processes.assistant.alpha',
                  defaultMessage: 'Alpha',
                })}
              </StatusPill>
            </AssistantStageSlot>
          </AssistantTitleGroup>
          <IconButton
            ariaLabel={intl.formatMessage({
              id: 'processes.assistant.close',
              defaultMessage: 'Hide the process assistant',
            })}
            icon={<MdChevronRight />}
            size="md"
            variant="ghost"
            onClick={onToggle}
            dataTestId={`${dataTestId}__close`}
          />
        </AssistantHeader>
      ) : (
        <AssistantRail>
          <IconButton
            ariaLabel={intl.formatMessage({
              id: 'processes.assistant.open',
              defaultMessage: 'Show the process assistant',
            })}
            icon={<MdAutoAwesome />}
            size="md"
            variant="emphasis"
            onClick={onToggle}
            dataTestId={`${dataTestId}__open`}
          />
          {/* The canvas stays frozen for the whole turn, collapsed or not, so
              the rail has to account for it. */}
          {busy && (
            <AssistantRailBusy
              title={intl.formatMessage({
                id: 'processes.assistant.working',
                defaultMessage: 'The process assistant is working…',
              })}
              data-testid={`${dataTestId}__rail-busy`}
            >
              <Spinner size="sm" />
            </AssistantRailBusy>
          )}
        </AssistantRail>
      )}
      {chatMounted && (
        // Hidden rather than unmounted while collapsed: the turn keeps running
        // server-side, and losing the chat would release the canvas mid-edit
        // and drop the save that refreshes it.
        <AssistantBody $hidden={!open}>
          <ProcessAssistantChat
            // Editing another process is a different conversation and a
            // different canvas; nothing the old one was mid-way through
            // should carry over into it.
            key={processId}
            processId={processId}
            onProcessEdited={onProcessEdited}
            onBusyChange={reportBusy}
            onBeforeSend={onBeforeSend}
            dataTestId={dataTestId}
          />
        </AssistantBody>
      )}
    </AssistantRoot>
  );
};

export default ProcessAssistantPanel;
